import { ItemType } from "@/types/enums/item-type";
import { GiftCardShopCartLine, PackageShopCartLine, ShopCartLine } from "@/types/shop";
import CatalogCartRow from "./catalog/cart-row";
import GiftCardCartRow from "./gift-cards/cart-row";
import MembershipCartRow from "./memberships/cart-row";
import PackageCartRow from "./packages/cart-row";

/**
 * Per-type cart line renderer. Each item type contributes one component that
 * paints the visual identity of one of its cart lines (icon, name, sublabel,
 * detail) and owns its own inline edit + remove affordances when supplied.
 *
 * `onReplace` and `onRemove` are passed through when the caller wants the
 * row to support each action; omit either for a read-only / non-removable
 * view. Both are rendered as a single grouped pill on the row's right
 * edge so styling stays consistent across surfaces. Quantity stepper +
 * subtotal chrome still belong to the host (cart drawer, etc.).
 *
 * Intentionally a plain lookup map rather than a method on the data adapter,
 * so per-type UI stays free to evolve independently from data plumbing.
 */
type CartLineRenderer<TLine extends ShopCartLine> = React.FC<{
  line: TLine;
  onReplace?: (line: TLine) => void;
  onRemove?: () => void;
}>;
const cartLineRenderers: Partial<{ [K in ItemType]: CartLineRenderer<ShopCartLine> }> = {
  [ItemType.GIFT_CARD]: GiftCardCartRow as CartLineRenderer<ShopCartLine>,
  [ItemType.PACKAGE]: PackageCartRow as CartLineRenderer<ShopCartLine>,
  [ItemType.MEMBERSHIP]: MembershipCartRow as CartLineRenderer<ShopCartLine>,
  [ItemType.SERVICE]: CatalogCartRow as CartLineRenderer<ShopCartLine>,
  [ItemType.PRODUCT]: CatalogCartRow as CartLineRenderer<ShopCartLine>,
  [ItemType.MEDICATION]: CatalogCartRow as CartLineRenderer<ShopCartLine>
};
export function renderCartLine(line: ShopCartLine, onReplace?: (line: ShopCartLine) => void, onRemove?: () => void): React.ReactNode {
  const Renderer = cartLineRenderers[line.itemType];
  if (!Renderer) return null;
  return <Renderer line={line} onReplace={onReplace} onRemove={onRemove} />;
}

// Re-exported so call sites that genuinely need the gift-card row directly
// (e.g. tests) don't have to reach into shop/gift-cards/.
export type { GiftCardShopCartLine, PackageShopCartLine };